import jsPDF from "jspdf";

/**
 * Generate a PDF checklist for the cycle with weekly checkboxes.
 */
export function exportCycleChecklistPDF(cycleData) {
  const pdf = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
  const pageWidth = pdf.internal.pageSize.getWidth();
  let y = 20;

  pdf.setFontSize(24);
  pdf.setTextColor(0, 200, 200);
  pdf.text("Peptide Cycle Plan", pageWidth / 2, y, { align: "center" });
  y += 15;

  pdf.setFontSize(14);
  pdf.setTextColor(150, 150, 150);
  pdf.text(`Goal: ${cycleData.goal}`, 20, y);
  y += 10;

  pdf.setFontSize(12);
  pdf.setTextColor(100, 100, 100);
  const summaryLines = pdf.splitTextToSize(cycleData.summary, pageWidth - 40);
  pdf.text(summaryLines, 20, y);
  y += summaryLines.length * 6 + 10;

  pdf.setFontSize(11);
  pdf.setTextColor(0, 0, 0);
  pdf.text(`Duration: ${cycleData.total_duration_weeks} weeks`, 20, y);
  pdf.text(`Total Cost: $${cycleData.total_cost_usd}`, 100, y);
  y += 15;

  pdf.setFontSize(16);
  pdf.setTextColor(0, 200, 200);
  pdf.text("Daily Tracking Checklist", 20, y);
  y += 10;

  cycleData.peptides?.forEach((peptide) => {
    if (y > 270) { pdf.addPage(); y = 20; }
    pdf.setFontSize(12);
    pdf.setTextColor(0, 0, 0);
    pdf.text(`${peptide.name}`, 20, y);
    y += 6;
    pdf.setFontSize(10);
    pdf.setTextColor(100, 100, 100);
    const dose = peptide.dosage_mg ? `${peptide.dosage_mg}mg` : `${peptide.dosage_iu}IU`;
    pdf.text(`${dose} - ${peptide.timing} - ${peptide.frequency}`, 25, y);
    y += 8;
    pdf.setFontSize(9);
    pdf.setTextColor(50, 50, 50);
    for (let week = 1; week <= Math.min(peptide.cycle_duration_weeks, 12); week++) {
      const xPos = 20 + ((week - 1) % 6) * 30;
      const yPos = y + Math.floor((week - 1) / 6) * 8;
      pdf.rect(xPos, yPos - 3, 4, 4);
      pdf.text(`Week ${week}`, xPos + 6, yPos);
    }
    y += Math.ceil(peptide.cycle_duration_weeks / 6) * 8 + 10;
  });

  if (y > 240) { pdf.addPage(); y = 20; }
  pdf.setFontSize(16);
  pdf.setTextColor(0, 200, 200);
  pdf.text("Weekly Schedule", 20, y);
  y += 10;
  Object.entries(cycleData.weekly_schedule || {}).forEach(([day, injections]) => {
    if (y > 270) { pdf.addPage(); y = 20; }
    pdf.setFontSize(11);
    pdf.setTextColor(0, 0, 0);
    pdf.rect(20, y - 3, 4, 4);
    pdf.text(`${day}:`, 26, y);
    pdf.setFontSize(10);
    pdf.setTextColor(80, 80, 80);
    const injectionsText = Array.isArray(injections) ? injections.join(", ") : injections;
    const injLines = pdf.splitTextToSize(injectionsText, pageWidth - 60);
    pdf.text(injLines, 50, y);
    y += injLines.length * 5 + 5;
  });

  if (y > 240) { pdf.addPage(); y = 20; }
  pdf.setFontSize(14);
  pdf.setTextColor(0, 200, 200);
  pdf.text("Tips for Success", 20, y);
  y += 8;
  cycleData.tips?.forEach((tip) => {
    if (y > 270) { pdf.addPage(); y = 20; }
    pdf.setFontSize(10);
    pdf.setTextColor(80, 80, 80);
    pdf.rect(20, y - 3, 4, 4);
    const tipLines = pdf.splitTextToSize(tip, pageWidth - 50);
    pdf.text(tipLines, 26, y);
    y += tipLines.length * 5 + 3;
  });

  pdf.setFontSize(8);
  pdf.setTextColor(150, 150, 150);
  pdf.text("Generated by PeptidePlanner AI - For research purposes only", pageWidth / 2, 290, { align: "center" });

  pdf.save(`peptide-cycle-checklist-${cycleData?.goal?.replace(/\s+/g, '-').toLowerCase() || 'plan'}.pdf`);
}

/**
 * Generate a comprehensive protocol PDF with shopping list, weekly schedule, tips.
 */
export function exportProtocolPDF(cycleData) {
  const pdf = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
  const pageWidth = pdf.internal.pageSize.getWidth();
  const pageHeight = pdf.internal.pageSize.getHeight();
  const margin = 15;
  let yPos = 20;

  const checkPageBreak = (required) => {
    if (yPos + required > pageHeight - margin) { pdf.addPage(); yPos = 20; }
  };

  pdf.setFontSize(24);
  pdf.setTextColor(0, 200, 200);
  pdf.text("Peptide Protocol Plan", margin, yPos);
  yPos += 10;

  pdf.setFontSize(14);
  pdf.setTextColor(100, 100, 100);
  pdf.text(`Goal: ${cycleData.goal || "Custom Protocol"}`, margin, yPos);
  yPos += 8;

  if (cycleData.summary) {
    pdf.setFontSize(10);
    pdf.setTextColor(60, 60, 60);
    const summaryLines = pdf.splitTextToSize(cycleData.summary, pageWidth - margin * 2);
    pdf.text(summaryLines, margin, yPos);
    yPos += summaryLines.length * 5 + 5;
  }

  checkPageBreak(30);
  pdf.setFillColor(240, 240, 240);
  pdf.rect(margin, yPos, pageWidth - margin * 2, 25, 'F');
  pdf.setFontSize(11);
  pdf.setTextColor(0, 0, 0);
  pdf.text(`Duration: ${cycleData.total_duration_weeks} weeks`, margin + 5, yPos + 8);
  pdf.text(`Total Cost: $${cycleData.total_cost_usd}`, margin + 5, yPos + 16);
  pdf.text(`Peptides: ${cycleData.peptides?.length || 0}`, margin + 80, yPos + 8);
  pdf.text(`Generated: ${new Date().toLocaleDateString()}`, margin + 80, yPos + 16);
  yPos += 32;

  checkPageBreak(20);
  pdf.setFontSize(16);
  pdf.setTextColor(0, 150, 150);
  pdf.text("Peptide Details", margin, yPos);
  yPos += 10;

  cycleData.peptides?.forEach((peptide, index) => {
    checkPageBreak(60);
    pdf.setFontSize(13);
    pdf.setTextColor(0, 0, 0);
    pdf.text(`${index + 1}. ${peptide.name}`, margin, yPos);
    yPos += 7;
    pdf.setFontSize(9);
    pdf.setTextColor(60, 60, 60);
    const dosage = peptide.dosage_mg ? `${peptide.dosage_mg}mg` : peptide.dosage_iu ? `${peptide.dosage_iu}IU` : "As directed";
    pdf.text(`Dosage: ${dosage} | Frequency: ${peptide.frequency}`, margin + 5, yPos);
    yPos += 5;
    pdf.text(`Duration: ${peptide.cycle_duration_weeks} weeks | Vials: ${peptide.vials_needed} | Cost: $${peptide.total_cost_usd}`, margin + 5, yPos);
    yPos += 5;
    pdf.text(`Timing: ${peptide.timing}`, margin + 5, yPos);
    yPos += 5;
    if (peptide.reconstitution) {
      pdf.text(`Reconstitution: ${peptide.reconstitution.bac_water_ml}ml BAC water → ${peptide.reconstitution.concentration_per_ml}`, margin + 5, yPos);
      yPos += 5;
    }
    yPos += 5;
  });

  checkPageBreak(40);
  pdf.setFontSize(16);
  pdf.setTextColor(0, 150, 150);
  pdf.text("Weekly Schedule", margin, yPos);
  yPos += 10;
  if (cycleData.weekly_schedule) {
    pdf.setFontSize(9);
    Object.entries(cycleData.weekly_schedule).forEach(([day, doses]) => {
      if (doses && doses.length > 0) {
        checkPageBreak(10);
        pdf.setTextColor(0, 0, 0);
        pdf.text(`${day}:`, margin, yPos);
        pdf.setTextColor(60, 60, 60);
        pdf.text(doses.join(" | "), margin + 25, yPos);
        yPos += 5;
      }
    });
  }
  yPos += 5;

  checkPageBreak(40);
  pdf.setFontSize(16);
  pdf.setTextColor(0, 150, 150);
  pdf.text("Injection Instructions", margin, yPos);
  yPos += 10;
  if (cycleData.beginner_guide) {
    pdf.setFontSize(9);
    pdf.setTextColor(60, 60, 60);
    cycleData.beginner_guide.injection_tips?.forEach((tip) => {
      checkPageBreak(8);
      const tipLines = pdf.splitTextToSize(`• ${tip}`, pageWidth - margin * 2 - 5);
      pdf.text(tipLines, margin + 5, yPos);
      yPos += tipLines.length * 4 + 2;
    });
  }
  yPos += 5;

  if (cycleData.starter_purchase_plan) {
    checkPageBreak(40);
    pdf.setFontSize(16);
    pdf.setTextColor(0, 150, 0);
    pdf.text("Starter Purchase Plan", margin, yPos);
    yPos += 8;
    pdf.setFontSize(9);
    pdf.setTextColor(60, 60, 60);
    pdf.text("Start with minimal upfront cost - buy week by week:", margin, yPos);
    yPos += 6;
    cycleData.starter_purchase_plan.weeks?.slice(0, 4).forEach((week) => {
      checkPageBreak(10);
      pdf.setTextColor(0, 0, 0);
      pdf.text(`Week ${week.week}: `, margin, yPos);
      pdf.setTextColor(60, 60, 60);
      const items = week.peptides_to_buy?.length > 0 ? week.peptides_to_buy.join(", ") : "No purchase needed";
      pdf.text(`${items} ($${week.estimated_cost || 0})`, margin + 20, yPos);
      yPos += 5;
    });
    yPos += 3;
    pdf.setFontSize(10);
    pdf.setTextColor(0, 100, 0);
    pdf.text(`Minimum to Start: $${cycleData.starter_purchase_plan.minimum_start_cost || 0}`, margin, yPos);
    yPos += 8;
  }

  checkPageBreak(40);
  pdf.setFontSize(16);
  pdf.setTextColor(0, 150, 150);
  pdf.text("Shopping List", margin, yPos);
  yPos += 10;
  pdf.setFontSize(9);
  pdf.setTextColor(0, 0, 0);
  pdf.text("Peptide", margin, yPos);
  pdf.text("Vials", margin + 60, yPos);
  pdf.text("$/Vial", margin + 85, yPos);
  pdf.text("Total", margin + 110, yPos);
  yPos += 5;
  pdf.setDrawColor(200, 200, 200);
  pdf.line(margin, yPos, pageWidth - margin, yPos);
  yPos += 3;
  pdf.setTextColor(60, 60, 60);
  cycleData.peptides?.forEach((peptide) => {
    checkPageBreak(8);
    pdf.text(peptide.name, margin, yPos);
    pdf.text(String(peptide.vials_needed), margin + 60, yPos);
    pdf.text(`$${peptide.cost_per_vial_usd}`, margin + 85, yPos);
    pdf.text(`$${peptide.total_cost_usd}`, margin + 110, yPos);
    yPos += 5;
  });
  yPos += 2;
  pdf.setDrawColor(0, 150, 150);
  pdf.line(margin, yPos, pageWidth - margin, yPos);
  yPos += 5;
  pdf.setFontSize(11);
  pdf.setTextColor(0, 100, 0);
  pdf.text(`TOTAL: $${cycleData.total_cost_usd}`, margin + 100, yPos);
  yPos += 10;

  checkPageBreak(30);
  pdf.setFontSize(12);
  pdf.setTextColor(0, 150, 150);
  pdf.text("Important Tips", margin, yPos);
  yPos += 7;
  pdf.setFontSize(8);
  pdf.setTextColor(60, 60, 60);
  cycleData.tips?.slice(0, 5).forEach((tip) => {
    checkPageBreak(6);
    const tipLines = pdf.splitTextToSize(`✓ ${tip}`, pageWidth - margin * 2 - 5);
    pdf.text(tipLines, margin, yPos);
    yPos += tipLines.length * 3.5 + 1;
  });

  pdf.setFontSize(8);
  pdf.setTextColor(150, 150, 150);
  pdf.text("Generated by PlanMyPeptides.com | For research purposes only", margin, pageHeight - 10);
  pdf.text("Order peptides from Summit Peptides · Andre Pereira · +1 (718) 717-3499", margin, pageHeight - 6);

  pdf.save(`peptide-protocol-${cycleData?.goal?.replace(/\s+/g, '-').toLowerCase() || 'plan'}-${new Date().toISOString().split('T')[0]}.pdf`);
}
